import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import {
  companyLinks,
  getHeaderGeItems,
  headerItems,
  home,
  loginGePageLink,
  loginLink,
  openPickPointLink,
  partnerLinks,
} from 'sections/PromoSection/pages/PromoPage/data'
import { MenuItemType, PromoPageKeys } from 'sections/PromoSection/pages/PromoPage/types'
import { LangSwitcher, TextPromo } from 'components/ui'
import { Href } from 'components/ui/Href/Href'
import { Flex } from 'components/uiKit/Flex/Flex'
import { sendAdriverEvent } from 'utils/adriver/sendAdriverEvent'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { isLanguage } from 'utils/helpers/i18n/getCurrentLanguage'
import { scrollToId } from 'utils/helpers/scrollToId'
import { sendEvent } from 'utils/helpers/sendEvent'
import { useResize } from 'utils/hooks/useResize'
import { AuthRoutesEnum, LKRoutesEnum, PromoRoutesEnum } from 'shared/constants/routes'
import { URLS } from 'shared/constants/urls'
import { SupportedLanguages } from 'shared/types/types'
import { AdriverEventType, EventType } from 'types/types'

import { SideMenu } from '../../sections/PromoSection/shared/SideMenu/SideMenu'

import BurgerIcon from 'assets/icons/burgerNew.svg?react'
import CrossIcon from 'assets/icons/cross.svg?react'
import LogoIcon from 'assets/icons/logo/logo-new-white.svg?react'
import LogoIconGe from 'assets/icons/logo/logo-new-white-ge.svg?react'
import UserAuthIcon from 'assets/icons/userAuthIcon.svg?react'
import UserIcon from 'assets/icons/userIcon.svg?react'
import Arrow from 'assets/img/shared/arrow2.svg?react'
import s from './Header.module.scss'

const HEADER_HEIGHT = 106

export const Header: FC = observer(() => {
  const { isTablet, isMobile, isSDesktop } = useResize()
  const location = useLocation()
  const navigate = useNavigate()
  const loginMenuRef = useRef<HTMLUListElement | null>(null)
  const loginButtonRef = useRef<HTMLLIElement | null>(null)
  const [sideMenuOpened, setSideMenuOpened] = useState<boolean>(false)
  const [loginSideMenuOpened, setLoginSideMenuOpened] = useState<boolean>(false)
  const [repairMenuOpened, setRepairMenuOpened] = useState<boolean>(false)
  const [bodyTopPoint, setBodyTopPoint] = useState<number>(0)
  const { t } = useTranslation()

  const { appStore } = useStore()

  const isSimplifiedPage = location.pathname === PromoRoutesEnum.simplifiedConditions

  const getHeaderKey = (key: string) => `${PromoPageKeys.header}.${key}`
  const getSharedKey = (key: string) => `${PromoPageKeys.shared}.${key}`

  const isOpenPvzLinkVisible = bodyTopPoint < -HEADER_HEIGHT * 10

  const additionalActions = (
    <Flex align="center" gap={16} className={s.additionalActionsContainer}>
      {isGeDomain() && !isSDesktop && <LangSwitcher />}
      <li key={loginLink.id} className={s.loginButton} onClick={() => toggleLoginSideMenu()} ref={loginButtonRef}>
        <div
          className={classNames(
            s.link,
            s.loginLink,
            appStore?.user.authenticated && s.authenticatedLink,
            loginSideMenuOpened && s.authenticatedLinkActive,
          )}
        >
          {loginSideMenuOpened ? <UserAuthIcon className={s.userIcon} /> : <UserIcon className={s.userIcon} />}
        </div>
        {loginSideMenuOpened && (
          <ul className={s.subMenuLogin} ref={loginMenuRef}>
            <Flex vertical>
              <Href
                href={isGeDomain() ? loginLink.path : loginGePageLink.path}
                onClick={(e) =>
                  isGeDomain()
                    ? appStore?.user.authenticated
                      ? handleSelectMenuItem(e, loginLink)
                      : handleSelectMenuItem(e, loginGePageLink)
                    : handleSelectMenuItem(e, loginLink)
                }
                className={s.subMenuLink}
                linkType="pathname"
              >
                <Flex vertical>
                  <TextPromo size="s20" fontFamily="ASLHauss" color="white" style={{ cursor: 'pointer' }}>
                    {t(getHeaderKey('Открыть пункт выдачи'))}
                  </TextPromo>
                  <TextPromo size="s14" fontFamily="ASLHauss" color="white" style={{ cursor: 'pointer' }}>
                    {t(getHeaderKey('Ваши заявки'))}
                  </TextPromo>
                </Flex>
              </Href>
              <Href
                href={isGeDomain() ? URLS.pvzLkGe : URLS.pvzLk}
                onClick={() => setLoginSideMenuOpened(false)}
                className={s.subMenuLink}
              >
                <Flex vertical>
                  <TextPromo size="s20" fontFamily="ASLHauss" color="white" style={{ cursor: 'pointer' }}>
                    {t(getHeaderKey('Работать с пунктом выдачи'))}
                  </TextPromo>
                  <TextPromo size="s14" fontFamily="ASLHauss" color="white" style={{ cursor: 'pointer' }}>
                    {t(getHeaderKey('Войти как партнер в личный кабинет'))}
                  </TextPromo>
                </Flex>
              </Href>
            </Flex>
          </ul>
        )}
      </li>
    </Flex>
  )

  const logoIcon = isLanguage(SupportedLanguages.GE) ? (
    <LogoIconGe className={`${s.logo} ${s.logoGe}`} onClick={(e: React.MouseEvent) => handleSelectMenuItem(e, home)} />
  ) : (
    <LogoIcon className={s.logo} onClick={(e: React.MouseEvent) => handleSelectMenuItem(e, home)} />
  )

  const headerGeItems = getHeaderGeItems()

  const renderRepairMenu = () => (
    <ul className={s.subMenu}>
      {companyLinks.map((link) => (
        <li key={link.id} className={s.subMenuItem}>
          <Href
            className={classNames(s.subMenuLink, location.pathname === link.path && s.activeLink)}
            href={link.path}
            onClick={(e) => handleSelectMenuItem(e, link)}
          >
            {link.label}
          </Href>
        </li>
      ))}
      {partnerLinks.map((link) => (
        <li key={link.id} className={s.subMenuItem}>
          <Href
            className={classNames(s.subMenuLink, location.pathname === link.path && s.activeLink)}
            href={link.path}
            onClick={(e) => handleSelectMenuItem(e, link)}
          >
            {link.label}
          </Href>
        </li>
      ))}
    </ul>
  )

  const openPvzLinkItem = (
    <li key={openPickPointLink.id} className={s.openPvzLinkWrapper}>
      <Href
        className={classNames(s.openPvzLink)}
        href={appStore?.user.authenticated ? loginLink.path : openPickPointLink.path}
        onClick={(e) => {
          appStore?.user.authenticated
            ? handleSelectMenuItem(e, { ...loginLink, label: 'Открыть ПВЗ' })
            : handleSelectMenuItem(e, openPickPointLink)
          sendAdriverEvent(AdriverEventType.openPVZHeader)
        }}
      >
        {openPickPointLink.label}
      </Href>
    </li>
  )

  const appsLink = partnerLinks[2]

  useEffect(() => {
    const handleClickOutside = () => {
      if (sideMenuOpened) {
        setSideMenuOpened(false)
      }
    }

    if (sideMenuOpened) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [sideMenuOpened])

  useEffect(() => {
    const handleClickOutsideLogin = (event: MouseEvent) => {
      if (
        loginSideMenuOpened &&
        loginMenuRef.current &&
        !loginMenuRef.current.contains(event.target as Node) &&
        loginButtonRef.current &&
        !loginButtonRef.current.contains(event.target as Node)
      ) {
        setLoginSideMenuOpened(false)
      }
    }

    if (loginSideMenuOpened) {
      document.addEventListener('mousedown', handleClickOutsideLogin)
    } else {
      document.removeEventListener('mousedown', handleClickOutsideLogin)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideLogin)
    }
  }, [loginSideMenuOpened])

  const toggleSideMenu = () => setSideMenuOpened((prev) => !prev)
  const toggleRepairMenu = () => setRepairMenuOpened((prev) => !prev)
  const toggleLoginSideMenu = () => setLoginSideMenuOpened((prev) => !prev)

  const handleSelectMenuItem = (e: React.MouseEvent, link: MenuItemType) => {
    e.preventDefault()

    if (link.type === 'anchor') {
      if (isSimplifiedPage && link === openPickPointLink) {
        scrollToId(link.path)
      } else {
        navigate(`/${link.path}`, { replace: false })
      }
    } else if (link.type === 'link') {
      window.open(link.path, '_blank')
    } else {
      if (link.path === LKRoutesEnum.orders && appStore.needRegistration) {
        navigate(AuthRoutesEnum.registration, { replace: false })
      } else {
        navigate(link.path, { replace: false })
      }
    }
    sendEvent(EventType.HeaderT, { name: link.label })
  }

  const handleScroll = () => {
    const bodyTopPointValue = window.document.body.getBoundingClientRect().top
    setBodyTopPoint(bodyTopPointValue)
    setSideMenuOpened(false)
    setRepairMenuOpened(false)
    setLoginSideMenuOpened(false)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const renderLinks = () => {
    return (
      <>
        {isGeDomain() ? (
          headerGeItems.map((link) => (
            <li key={link.id} className={s.menuItem}>
              <Href key={link.id} className={s.link} href={link.path} onClick={(e) => handleSelectMenuItem(e, link)}>
                {t(getHeaderKey(link.label))}
              </Href>
            </li>
          ))
        ) : (
          <>
            {headerItems.map((link) => (
              <li key={link.id} className={s.menuItem}>
                <Href
                  key={link.id}
                  className={classNames(s.link, location.pathname === link.path && s.activeLink)}
                  href={link.path}
                  onClick={(e) => handleSelectMenuItem(e, link)}
                >
                  {link.label}
                </Href>
              </li>
            ))}
            {!isMobile && !isTablet && !isSDesktop && (
              <li
                className={classNames(s.menuItem, { [s.menuItemActive]: repairMenuOpened })}
                onClick={toggleRepairMenu}
              >
                <TextPromo className={s.link}>
                  Ремонт и оформление <Arrow className={s.arrow} />
                </TextPromo>
                {repairMenuOpened && renderRepairMenu()}
              </li>
            )}
            {isOpenPvzLinkVisible && !sideMenuOpened && openPvzLinkItem}
          </>
        )}
        {isGeDomain() && (isMobile || isTablet) && (
          <li key={appsLink.id} className={s.menuItem}>
            <Href
              key={appsLink.id}
              className={s.link}
              href={appsLink.path}
              onClick={(e) => handleSelectMenuItem(e, appsLink)}
            >
              {t(getSharedKey(appsLink.label))}
            </Href>
          </li>
        )}
      </>
    )
  }

  return (
    <header className={s.Header}>
      {isSDesktop ? (
        <nav className={s.menu}>
          {logoIcon}
          <Flex gap={12} align="center">
            {sideMenuOpened ? (
              <CrossIcon className={s.cross} onClick={toggleSideMenu} />
            ) : (
              <Flex gap={16}>
                {isGeDomain() && <LangSwitcher />}
                <BurgerIcon className={s.burger} onClick={toggleSideMenu} />
              </Flex>
            )}
            {additionalActions}
          </Flex>

          <SideMenu isOpen={sideMenuOpened} topPosition={115}>
            {renderLinks()}
            {!isGeDomain() && renderRepairMenu()}
            {openPvzLinkItem}
          </SideMenu>
        </nav>
      ) : (
        <>
          {logoIcon}
          <nav className={s.navigation}>
            <ul className={s.linksWrapper}>
              {renderLinks()}
              {additionalActions}
            </ul>
          </nav>
        </>
      )}
    </header>
  )
})
